import {
  THeatmapEventsResponseTripItemEvent,
  THeatmapTrip,
} from "../../api/types/Heatmap";
import svgIcons from "../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../constants/constants";
import { TMapType } from "../../types/map";
import {
  lineSymbolForDashedLine,
  lineSymbolForDottedLine,
} from "../map-asset/map-elements/helper";
import { RenderRouteDefault } from "../map-asset/map-elements/route-default";
import { RenderRouteDirectionsService } from "../map-asset/map-elements/route-directionsservice";
import { RenderRoutePointToPoint } from "../map-asset/map-elements/route-pointtopoint";
import { RenderRouteSnapToRoad } from "../map-asset/map-elements/route-snaptoroad";
import { RenderTripPoints } from "../map-asset/map-elements/trip-points";
import {
  TMapRouteViewsEnabled,
  TRenderRouteClassParams,
} from "../map-asset/map-elements/type";
import { mapRouteClickInfoBoxContent } from "./common";
import {
  TMapOperations,
  TMapOperationsPropHeatmapParams,
  TMapOperationsProps,
  TMapUpdatesHandler,
  TVehicleDataPoint,
} from "./type";

const debug = false;

export const mapOperations: TMapOperations = (props, filteredDataPoints) => {
  if (APP_CONFIG.DEBUG.MAPS)
    console.log("== MAPOP", props.dataPoints.length, filteredDataPoints.length);
  if (props.dataPoints.length === 0) return; // prevents the `Cannot read properties of null (reading 'getCurrentCrsViewport')` error
  //(window as any).map = props.mapRef.current;
  const google = (window as any).google;

  // create a namespace for map objects
  props.mapRef.current.objects = {
    mTripLayers: [],
    mDirectionsManagers: [],
    mTripsVisibility: [],
    mWaypoints: [],
    mPushpins: [],
    mClusterLayer: null,
    mTripsData: [],
  };

  const renderMapObjects = async () => {
    const MAX_WAYPOINTS = 25;
    const MAX_VIAPOINTS = 10;
    const G_MAX_WAYPOINTS = 25;
    const { AdvancedMarkerElement } =
      await google?.maps?.importLibrary("marker");
    const map = props.mapRef.current.map;
    // determine snapped points and save them to LS - to prevent repeated calls
    // finally use snapped points to create waypoints

    const parseSvgToDom = (svgString: string) => {
      if (!svgString.startsWith("<svg") && APP_CONFIG.DEBUG.MAPS)
        console.error("[[ SVG ERROR ]] SVG DID NOT START WITH <svg", svgString);
      const parser = new DOMParser();
      return parser.parseFromString(svgString, "image/svg+xml").documentElement;
    };

    // route click handler - show icon on clicked position with popup
    const routeClickHandler = (e: any, _heatmapTrip: THeatmapTrip) => {
      console.log("clicked", e);
      const latLng = e?.latLng;
      const pinSvg = parseSvgToDom(svgIcons.heatmapRouteClickIconAsString);
      if (!(window as any).routeClickMarker) {
        (window as any).routeClickMarker = new AdvancedMarkerElement({
          position: {
            lat: latLng.lat(),
            lng: latLng.lng(),
          },
          map: map,
          content: pinSvg,
        });
        (window as any).routeClickMarker.content.style.transform =
          "translateY(50%)";
      }
      (window as any).routeClickMarker.position = latLng;
      (window as any).routeClickMarker.setMap(map);
      // info window
      if (!(window as any).routeClickInfowindow) {
        (window as any).routeClickInfowindow = new google.maps.InfoWindow({
          content: mapRouteClickInfoBoxContent(
            _heatmapTrip // .end_timestamp, // `${humanReadableTimeDuration(dbTimeToJsTime(_heatmapTrip.end_timestamp))}`,
          ),
          position: (window as any).routeClickMarker.position,
          headerDisabled: true,
          // pixelOffset: new google.maps.Size(0, -20),
          zIndex: APP_CONFIG.MAPS.INFOBOX_ZINDEX,
        });
      }
      (window as any).routeClickInfowindow.setContent(
        mapRouteClickInfoBoxContent(
          _heatmapTrip // .end_timestamp, // `${humanReadableTimeDuration(dbTimeToJsTime(_heatmapTrip.end_timestamp))}`,
        )
      );
      (window as any).routeClickInfowindow.open({
        anchor: (window as any).routeClickMarker,
        map: map,
      });
    };

    // on clicking anywhere on the map - hide icon and popup
    google.maps.event.addListener(map, "click", function (e: any) {
      (window as any)?.routeClickMarker?.setMap(null);
      (window as any)?.routeClickInfowindow?.setMap(null);
    });

    props.dataPoints
      // .filter((tripPoints) => tripPoints.length > 1) // disabled 6/30/2025, this line was filtering out trips with single trip point resulting in mismatched / incorrect trip points being displayed, opening trip 5 was displaying points from trip 6 - app-wide issue reported by client
      .forEach((tripPoints, tripIndex) => {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log(
            "===================== tripPoints",
            tripPoints.events.length
          );

        // initialize mTripsData for the trip index
        const viewInitValue = {
          classObject: null,
          error: false,
          mapElements: [],
          visible: true,
        };
        const { routeViewsEnabled } = props.mapRef.current;
        if (!props.mapRef.current.objects.mTripsData[tripIndex]) {
          props.mapRef.current.objects.mTripsData[tripIndex] = {
            tripIndex,
            originalPoints: tripPoints,
            views: {
              MARKERS: viewInitValue,
              ROUTE_POINT_TO_POINT: {
                ...viewInitValue,
                visible: routeViewsEnabled.POINT_TO_POINT,
              },
              ROUTE_SNAP_TO_ROAD: {
                ...viewInitValue,
                visible: routeViewsEnabled.SNAP_TO_ROAD,
              },
              ROUTE_DIRECTIONS_SERVICE: {
                ...viewInitValue,
                visible: routeViewsEnabled.DIRECTIONS_SERVICE,
              },
              ROUTE_DEFAULT: {
                ...viewInitValue,
                visible: routeViewsEnabled.DEFAULT,
              },
            },
          };
        }

        // render the trip points and routes for the trip
        const commonParams: TRenderRouteClassParams<
          THeatmapEventsResponseTripItemEvent,
          typeof props
        > = {
          props,
          tripIndex,
          sanitizedTripPoints: tripPoints.events,
          polylineConfig: {
            strokeColor: applicableRouteColor(
              props.heatmapParams,
              tripPoints.end_timestamp
            ),
            strokeOpacity: 1,
            strokeWeight: 3,
          },
        };

        // trip points renderer
        const tripPointsRenderer = new RenderTripPoints({
          ...commonParams,
          useDotsForStartEnd: true,
        } as any);
        props.mapRef.current.objects.mTripsData[
          tripIndex
        ].views.MARKERS.classObject = tripPointsRenderer;
        tripPointsRenderer.render();

        // route renderer - point to point (without snapping)
        const routeRendererP2P = new RenderRoutePointToPoint({
          ...commonParams,
          polylineConfig: {
            ...commonParams.polylineConfig,
            strokeOpacity: 0,
            icons: [
              {
                icon: lineSymbolForDashedLine,
                offset: "0",
                repeat: "10px",
              },
            ],
          },
        } as any);
        props.mapRef.current.objects.mTripsData[
          tripIndex
        ].views.ROUTE_POINT_TO_POINT.classObject = routeRendererP2P;
        routeRendererP2P.render();

        // route renderer - snap to road
        const routeRendererSnapToRoad = new RenderRouteSnapToRoad(
          commonParams as any
        );
        props.mapRef.current.objects.mTripsData[
          tripIndex
        ].views.ROUTE_SNAP_TO_ROAD.classObject = routeRendererSnapToRoad;
        routeRendererSnapToRoad.render();

        // route renderer - directions service
        const routeRendererDirectionsService = new RenderRouteDirectionsService(
          {
            ...commonParams,
            polylineConfig: {
              ...commonParams.polylineConfig,
              strokeOpacity: 0,
              icons: [
                {
                  icon: lineSymbolForDottedLine,
                  offset: "0",
                  repeat: "5px",
                },
              ],
            },
          } as any
        );
        props.mapRef.current.objects.mTripsData[
          tripIndex
        ].views.ROUTE_DIRECTIONS_SERVICE.classObject =
          routeRendererDirectionsService;
        routeRendererDirectionsService.render();

        // route renderer - default
        const routeRendererDefault = new RenderRouteDefault({
          ...commonParams,
          polylineConfig: {
            ...commonParams.polylineConfig,
            strokeWeight: 5,
          },
        } as any);
        props.mapRef.current.objects.mTripsData[tripIndex].views.ROUTE_DEFAULT =
          {
            ...viewInitValue,
            classObject: routeRendererDefault,
          };
        routeRendererDefault.render();
      });

    // center the map to the polygon
    setTimeout(() => mapCenterToDataPoints(props, props.dataPoints), 1000);

    setTimeout(() => {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log("assuming that all map operations are finished by now");
      (window as any).map = props.mapRef.current;
    }, 5000);

    return null;
  };
  renderMapObjects();
};

export const mapUpdatesHandler: TMapUpdatesHandler = async (
  props,
  action,
  value,
  additionalData
) => {
  const google = window.google;

  if (APP_CONFIG.DEBUG.MAPS && APP_CONFIG.DEBUG.VERBOSE) {
    console.log("[mapUpdatesHandler]", action, value);
  }

  switch (action) {
    case "centerToPushpin":
      if (value && value.length > 0) {
        mapCenterToDataPoints(props, value as THeatmapTrip[]);
        return;
      }

      const pushpinId = value as number;

      // Find the pushpin by ID
      const pushpinObject = props.mapRef.current.objects.mPushpins.find(
        (pushpinObject) => pushpinObject.recordId === pushpinId
      );

      if (pushpinObject) {
        props.mapRef.current.map.setOptions({
          center: new google.maps.LatLng(
            pushpinObject.marker.position.lat(),
            pushpinObject.marker.position.lng()
          ),
        });
        props.mapRef.current.map.setZoom(props.mapRef.current.map.getZoom()); // Maintain current zoom
      }
      break;

    case "centerToCoords":
      const coords = value as { center: number[]; zoom: number };
      if (coords.center.length === 2) {
        props.mapRef.current.map.setOptions({
          center: new google.maps.LatLng(coords.center[0], coords.center[1]),
        });
        props.mapRef.current.map.setZoom(coords.zoom ?? 5);
      }
      break;

    case "setTripHoverState":
      const tripIndex = value as number | null;

      // // Hide or show trips based on selection
      // props.mapRef.current?.objects?.mTripsVisibility.forEach((_, index) => {
      //   // Hide or show trip markers based on selection
      //   props.mapRef.current?.objects?.mPushpins.forEach((pushpinObject) => {
      //     pushpinObject.marker.setMap(
      //       tripIndex === null || pushpinObject.tripIndex === tripIndex
      //         ? props.mapRef.current.map
      //         : null
      //     );
      //   });

      //   // Hide or show trip layer based on selection (tripLayer is actually a polyline in Google Maps)
      //   const tripLayers = props.mapRef.current?.objects?.mTripLayers?.[index];
      //   if (tripLayers) {
      //     tripLayers.forEach((tripLayer: any) => {
      //       tripLayer.setMap(
      //         tripIndex === null || index === tripIndex
      //           ? props.mapRef.current.map
      //           : null
      //       );
      //     });
      //   }

      //   // Hide or show directions service based route on selection
      //   const directionsManager =
      //     props.mapRef.current?.objects?.mDirectionsManagers?.[index];
      //   if (directionsManager) {
      //     if (tripIndex === null) {
      //       // directionsManager?.calculateDirections();
      //       directionsManager?.setMap(props.mapRef.current.map);
      //     } else {
      //       if (index === tripIndex) {
      //         // directionsManager?.calculateDirections();
      //         directionsManager?.setMap(props.mapRef.current.map);
      //       } else {
      //         // directionsManager?.clearDisplay();
      //         directionsManager?.setMap(null);
      //       }
      //     }
      //   }
      // });

      const hoverStateRouteViewsEnabled: TMapRouteViewsEnabled =
        additionalData?.routeViewsEnabled || {
          DEFAULT: true,
          POINT_TO_POINT: true,
          SNAP_TO_ROAD: true,
          DIRECTIONS_SERVICE: true,
        };
      props.mapRef.current.routeViewsEnabled = hoverStateRouteViewsEnabled;

      props.mapRef.current?.objects?.mTripsData.forEach((tripData) => {
        const isSelected =
          tripIndex === null || tripData.tripIndex === tripIndex;

        // toggle trip points/markers visibility using the classObject of RenderTripPoints
        const tripPointsRenderer = tripData.views.MARKERS
          .classObject as RenderTripPoints;
        if (tripPointsRenderer) {
          if (isSelected) {
            tripPointsRenderer.show();
          } else {
            tripPointsRenderer.hide();
          }
        }
        // toggle route visibility using the classObject of RenderRoutePointToPoint
        const routeRendererP2P = tripData.views.ROUTE_POINT_TO_POINT
          .classObject as RenderRoutePointToPoint;
        if (routeRendererP2P) {
          // show only if: trip is selected AND checkbox is enabled
          if (isSelected && hoverStateRouteViewsEnabled.POINT_TO_POINT) {
            routeRendererP2P.show();
          } else {
            routeRendererP2P.hide();
          }
        }
        // toggle snap to road route visibility using the classObject of RenderRouteSnapToRoad
        const routeRendererSnapToRoad = tripData.views.ROUTE_SNAP_TO_ROAD
          .classObject as RenderRouteSnapToRoad;
        if (routeRendererSnapToRoad) {
          if (isSelected && hoverStateRouteViewsEnabled.SNAP_TO_ROAD) {
            routeRendererSnapToRoad.show();
          } else {
            routeRendererSnapToRoad.hide();
          }
        }
        // toggle directions service route visibility using the classObject of RenderRouteDirectionsService
        const routeRendererDirectionsService = tripData.views
          .ROUTE_DIRECTIONS_SERVICE.classObject as RenderRouteDirectionsService;
        if (routeRendererDirectionsService) {
          if (isSelected && hoverStateRouteViewsEnabled.DIRECTIONS_SERVICE) {
            routeRendererDirectionsService.show();
          } else {
            routeRendererDirectionsService.hide();
          }
        }
        // toggle default route visibility using the classObject of RenderRouteDefault
        const routeRendererDefault = tripData.views.ROUTE_DEFAULT
          .classObject as RenderRouteDefault;
        if (routeRendererDefault) {
          if (isSelected && hoverStateRouteViewsEnabled.DEFAULT) {
            routeRendererDefault.show();
          } else {
            routeRendererDefault.hide();
          }
        }
      });
      break;
    case "routeViewsChange":
      const routeViewsEnabled = value as TMapRouteViewsEnabled;
      console.log("routeViewsEnabled", routeViewsEnabled);
      props.mapRef.current.routeViewsEnabled = routeViewsEnabled;

      props.mapRef.current?.objects?.mTripsData.forEach((tripData) => {
        const routeRendererP2P = tripData.views.ROUTE_POINT_TO_POINT
          .classObject as RenderRoutePointToPoint;
        if (routeRendererP2P) {
          if (routeViewsEnabled.POINT_TO_POINT) {
            routeRendererP2P.show();
          } else {
            routeRendererP2P.hide();
          }
        }
        // toggle route renderer - snap to road
        const routeRendererSnapToRoad = tripData.views.ROUTE_SNAP_TO_ROAD
          .classObject as RenderRouteSnapToRoad;
        if (routeRendererSnapToRoad) {
          if (routeViewsEnabled.SNAP_TO_ROAD) {
            routeRendererSnapToRoad.show();
          } else {
            routeRendererSnapToRoad.hide();
          }
        }
        // toggle route renderer - directions service
        const routeRendererDirectionsService = tripData.views
          .ROUTE_DIRECTIONS_SERVICE.classObject as RenderRouteDirectionsService;
        if (routeRendererDirectionsService) {
          if (routeViewsEnabled.DIRECTIONS_SERVICE) {
            routeRendererDirectionsService.show();
          } else {
            routeRendererDirectionsService.hide();
          }
        }
        // toggle route renderer - default
        const routeRendererDefault = tripData.views.ROUTE_DEFAULT
          .classObject as RenderRouteDefault;
        if (routeRendererDefault) {
          if (routeViewsEnabled.DEFAULT) {
            routeRendererDefault.show();
          } else {
            routeRendererDefault.hide();
          }
        }
      });
      break;

    case "tripReplayEvents":
      if (APP_CONFIG.DEBUG.MAPS) {
        console.log("tripReplayEvents", value);
        console.log("PP", props.mapRef.current?.objects?.mPushpins);
      }

      const {
        /*tripIndex: tripReplayIndex,*/
        completeCallback,
        replayPercChangeCallback,
      } = value as {
        // tripIndex: number;
        completeCallback: () => void;
        replayPercChangeCallback: (perc: number | null) => void;
      };

      // hide all routes and markers
      props.dataPoints.forEach((_, i) => {
        // index [0] displays interpolated / google snap to road route
        props.mapRef.current.objects.mTripLayers?.[i]?.[0]?.setPath([]);
        // index [1] displays connected dots (system reported geo coordinates) route
        props.mapRef.current.objects.mTripLayers?.[i]?.[1]?.setPath([]);
        // hide markers
        props.mapRef.current.objects.mPushpins.forEach((mPushpin) => {
          mPushpin?.marker?.setMap(null);
        });
      });

      const actualTripDurationSec = 60 * 60 * 12; // 43,200
      const iterationChunk = 400;
      const totalIterations = actualTripDurationSec / iterationChunk; // 108 seconds
      (window as any).replayCounter = 0;
      (window as any).replayInterval = setInterval(() => {
        // ensure interval stops once expired
        if ((window as any).replayCounter >= totalIterations) {
          clearInterval((window as any).replayInterval);
          completeCallback?.();
          if (APP_CONFIG.DEBUG.MAPS)
            console.log(`== END TIMESTAMP`, props.heatmapParams.timestamp);
          return;
        }

        // do the replay...
        const refStartTimestamp =
          props.heatmapParams.timestamp -
          actualTripDurationSec +
          (window as any).replayCounter * iterationChunk;
        const iterationTimestamps = {
          start:
            refStartTimestamp + ((window as any).replayCounter === 0 ? 0 : 1),
          end: refStartTimestamp + iterationChunk,
        };
        if (APP_CONFIG.DEBUG.MAPS) {
          console.log("==CHECK==", props.dataPoints);
          console.log(
            `== [${(window as any).replayCounter}]`,
            iterationTimestamps,
            iterationTimestamps.end - iterationTimestamps.start
          );
        }
        // incrementally show route path and markers...
        props.dataPoints.forEach((heatmapTrip, tripIndex) => {
          const timeframePoints = heatmapTrip.events
            .filter((event) => {
              return (
                event.timestamp >=
                  props.heatmapParams.timestamp - actualTripDurationSec &&
                event.timestamp <= iterationTimestamps.end
              );
            })
            .map((pt) => ({
              lat: pt.latitude,
              lng: pt.longitude,
            }));
          if (tripIndex === 0 && APP_CONFIG.DEBUG.MAPS) {
            console.log("timeframePoints", timeframePoints);
          }
          // here we are making use of the index [tripIndex][0] which generally shows
          // interpolated / google snap to road route as that bears strokeOpacity = 1
          props.mapRef.current.objects.mTripLayers?.[tripIndex]?.[0]?.setPath(
            timeframePoints
          );
          // incrementally display markers
          props.mapRef.current.objects.mPushpins
            .filter(
              (mPushpin) =>
                mPushpin.tripIndex === tripIndex &&
                mPushpin.pointData.timestamp >=
                  props.heatmapParams.timestamp - actualTripDurationSec &&
                mPushpin.pointData.timestamp <= iterationTimestamps.end
            )
            .forEach((mPushpin) => {
              mPushpin?.marker?.setMap(props.mapRef.current.map);
            });
        });

        // update counter
        (window as any).replayCounter++;
        replayPercChangeCallback?.(
          (100 * (window as any).replayCounter) / totalIterations
        );
      }, 100 * 2); // THIS CONTROLS THE SPEED OF RENDER

      break;

    case "tripPointSelectionChange":
      const selectedTripPoint =
        value as THeatmapEventsResponseTripItemEvent | null;
      const tripPointRecordId = selectedTripPoint?.recordid || null;

      // Hide all pushpins except the selected one
      props.mapRef.current?.objects?.mPushpins.forEach((pushpinObject) => {
        pushpinObject.marker.setMap(
          tripPointRecordId === null ||
            pushpinObject.recordId === tripPointRecordId
            ? props.mapRef.current.map
            : null
        );
      });

      const selectedPointPushpinObject =
        props.mapRef?.current?.objects?.mPushpins.find(
          (pushpin) => pushpin.recordId === tripPointRecordId
        );

      if (selectedPointPushpinObject) {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log("selectedPointPushpinObject", selectedPointPushpinObject);
        // Center the map to the selected trip point
        props.mapRef?.current?.map.setOptions({
          center: new google.maps.LatLng(
            typeof selectedPointPushpinObject.marker.position.lat
              ? selectedPointPushpinObject.marker.position.lat
              : selectedPointPushpinObject.marker.position.lat(),
            typeof selectedPointPushpinObject.marker.position.lng
              ? selectedPointPushpinObject.marker.position.lng
              : selectedPointPushpinObject.marker.position.lng()
          ),
        });
        props.mapRef?.current?.map.setZoom(APP_CONFIG.MAPS.DEFAULTS.EVENT_ZOOM);
      } else {
        // Center the map to all visible pushpins
        mapCenterToDataPoints(props, props.dataPoints);
      }
      break;

    default:
      break;
  }
};

// center the map to the visible pushpins
// const mapCenterToDataPoints: TMapOperations = async(props, value) => {
//   //const Microsoft = (window as any).Microsoft;
//   const google = (window as any).google;
//   const { LatLngBounds } = await google.maps.importLibrary("core");
//   if (!props.dataPoints || props.dataPoints.length < 1) return;
//   //props.mapRef.current.map?.setView({
//     // bounds: Microsoft.Maps.LocationRect.fromLocations(
//     //   // visibleDataPoints
//     //   props.dataPoints
//     //     .reduce(
//     //       (t, i, index) =>
//     //         // filtering trip points based on selectedTripIndex
//     //         props?.selectedTripIndex !== null &&
//     //         index !== props?.selectedTripIndex
//     //           ? t
//     //           : [...t, ...i],
//     //       []
//     //     ) // flatten the array
//     //     .map(
//     //       (dataPoint) =>
//     //         new Microsoft.Maps.Location(dataPoint.latitude, dataPoint.longitude)
//     //     )
//     // ),
//     // padding: 30, // 5,
//     // animate: true,
//   //});
// };

const mapCenterToDataPoints: TMapOperations = async (props, value) => {
  const google = window.google;
  const googleMapsCore: any = await google?.maps?.importLibrary?.("core");
  if (!googleMapsCore || !googleMapsCore.LatLngBounds) return;
  const { LatLngBounds } = googleMapsCore;
  if (!props.dataPoints || props.dataPoints.length < 1) return;
  const bounds = new LatLngBounds();
  props.dataPoints
    .reduce((acc, trip, index) => {
      if (
        props?.selectedTripIndex === null ||
        index === props?.selectedTripIndex
      ) {
        acc.push(...trip.events);
      }
      return acc;
    }, [] as THeatmapEventsResponseTripItemEvent[])
    .forEach((dataPoint) => {
      bounds.extend(
        new google.maps.LatLng(dataPoint.latitude, dataPoint.longitude)
      );
    });
  if (!bounds.isEmpty()) {
    props.mapRef.current.map?.fitBounds(bounds, 20);
  }
};

export const mapVehicleStateIconSlug = (
  dataPoint: TVehicleDataPoint | undefined
) => {
  // if(!!dataPoint && dataPoint.coords.length > 2) return 'driving';
  // return !!dataPoint && dataPoint.is_active ? 'idle_active' : 'idle_inactive';
  return "mapVehicleStateIconSlug";
};

const mapGetPushpinCountWithinMapBounds = (props: TMapOperationsProps) => {
  const mapBounds = props.mapRef?.current?.map?.getBounds();
  if (!mapBounds) return 0;
  const pushpins = props.mapRef.current.objects.mPushpins;
  return pushpins.filter((pushpinObject) =>
    mapPushpinLiesWithinMapBounds(
      pushpinObject.marker,
      props.mapRef?.current?.map
    )
  ).length;
};

const mapPushpinLiesWithinMapBounds = (pushpin: any, mapObj: any) => {
  const mapBounds = mapObj.getBounds();
  if (!mapBounds) return false;
  return mapBounds.contains(pushpin.getLocation()) && pushpin.getVisible();
};

const handleMapViewChangeEnd = (props: TMapOperationsProps) => {
  const pushpinCountWithinMapBounds = mapGetPushpinCountWithinMapBounds(props);
  const showInfoboxes =
    pushpinCountWithinMapBounds <=
    APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD;
  if (APP_CONFIG.DEBUG.MAPS)
    console.log(
      `viewchangeend count: ${pushpinCountWithinMapBounds} showInfoboxes: ${showInfoboxes}`
    );

  // update mapState with new center and zoom
  if (!!props.mapRef.current.map?.getCenter) {
    props?.onViewChangeEnd?.(
      {
        latitude: props.mapRef.current.map?.getCenter().latitude,
        longitude: props.mapRef.current.map?.getCenter().longitude,
      },
      props.mapRef.current.map.getZoom()
    );
  }
};

export const updateEntitiesOnMapTypeChange = (
  props: TMapOperationsProps,
  mapType: TMapType
) => {
  if (APP_CONFIG.DEBUG.MAPS)
    console.warn("[updateEntitiesOnMapTypeChange] NOT IMPLEMENTED", mapType);
  // const pushpinCountWithinMapBounds = mapGetPushpinCountWithinMapBounds(props);
  // const showInfoboxes =
  //   pushpinCountWithinMapBounds <=
  //   APP_CONFIG.MAPS.SHOW_INFOBOX_PUSHPIN_THRESHOLD;
  // // hide infoboxes when map type is streetside / birdseye
  // if (APP_CONFIG.MAPS.SPECIAL_VIEW_TYPES.includes(mapType)) {
  //   props.mapRef.current.objects.mPushpins.forEach((pushpinObject) => {
  //     pushpinObject.infobox.setOptions({
  //       visible: false,
  //     });
  //   });
  // } else {
  //   updateInfoboxesVisibility(props as TMapOperationsProps);
  // }
  // // props.mapRef.current.objects.mPushpins.forEach((pushpinObject) => {
  // //   pushpinObject.infobox.setOptions({
  // //     visible: showInfoboxes && !APP_CONFIG.MAPS.SPECIAL_VIEW_TYPES.includes(mapType) // (mapType !== APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.STREETSIDE].type)
  // //   });
  // // });
  // // hide clusters when map type is streetside / birdseye
  // props.mapRef.current.objects.mClusterLayer?.setOptions({
  //   visible: !APP_CONFIG.MAPS.SPECIAL_VIEW_TYPES.includes(mapType), // (mapType !== APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.STREETSIDE].type)
  // });
};

const applicableRouteColor = (
  heatmapParams: TMapOperationsPropHeatmapParams,
  timestampEnd: number
) => {
  const hoursDifference =
    Math.abs(heatmapParams.timestamp - timestampEnd) / (60 * 60);
  if (APP_CONFIG.DEBUG.MAPS) console.log("hoursDifference", hoursDifference);
  let color = "";
  heatmapParams.heatmapConfig.legends.forEach((legendItem, i) => {
    if (
      hoursDifference >= legendItem.start_hour &&
      hoursDifference < legendItem.end_hour &&
      color === ""
    ) {
      color = legendItem.color;
    }
  });
  return color || "#000";
};
